"use strict";

/**
 * Client for the unified record-lock endpoints (`/sec/locks/*`).
 *
 * @remarks
 * WEB-4375: WEB-3336 replaced the per-entity DMS lock endpoints (a separate
 * acquire/ping pair nested under each sale) with one entity-agnostic family:
 *
 *   GET  sec/locks/acquire?entityType=…&entityId=…&lock_type=…
 *   GET  sec/locks/{lockName}/ping
 *   POST sec/locks/{lockName}/release
 *
 * The DMS Flutter app migrated at the time; Web2 did not, and the legacy pair
 * was kept alive purely for Web2 (blocking the server-side cleanup in
 * WEB-3352). This service is the single Web2 client for the new family so no
 * controller has to hand-build a lock URL again.
 *
 * Two behaviours differ from the legacy endpoints and are handled here:
 *
 * - The legacy pair had no release endpoint and used a 5-second lock expiry,
 *   so an abandoned lock cleared itself almost immediately. The unified family
 *   defaults to a 300-second expiry, so callers MUST release explicitly when
 *   the user leaves the locked screen — see {@link release} and
 *   {@link releaseOnUnload}.
 * - Acquire accepts `override=1`, which force-releases a lock held by another
 *   user. That is the correct mapping for the existing "this sale is being
 *   assessed by X, are you sure you want to proceed?" confirmation, which
 *   previously carried on with no lock at all.
 *
 * `device_uuid` is appended to every request by SSHttpService, so callers do
 * not need to pass it.
 */
class SSLockService {

    /**
     * @param {Object} $window       Angular window wrapper.
     * @param {Object} SSHttpService Showsuite HTTP client.
     */
    constructor($window, SSHttpService) {
        const self = this;

        self.$window = $window;
        self.SSHttpService = SSHttpService;

        /** Entity types accepted by `entityType` on the unified acquire. */
        self.ENTITY_TYPE_SALE = 'sale';
        self.ENTITY_TYPE_EOI = 'eoi';
        /**
         * WEB-4459 — the AMS (agent-side) Team Lead review steps. Separate from
         * the DMS types above because the server authorises them on the project
         * Team Lead role instead of a DMS permission, and accepts the record at
         * an earlier status.
         */
        self.ENTITY_TYPE_AMS_SALE = 'ams_sale';
        self.ENTITY_TYPE_AMS_EOI = 'ams_eoi';

        /** Record is held by another user; `data.locking_user` names them. */
        self.SUB_CODE_LOCK_CONFLICT = 1100001;
        /** Lock no longer exists — already released or expired. */
        self.SUB_CODE_LOCK_NOT_FOUND = 1100002;
        /** The caller's own lock lapsed; the review session is over. */
        self.SUB_CODE_LOCK_EXPIRED = 1100003;

        /**
         * Table prefix used by the server when it builds a lock name.
         *
         * The AMS entity types share a prefix with their DMS counterparts —
         * the lock type is what keeps the two locks on one record distinct
         * (e.g. `sales.12.10` for the DMS review, `sales.12.40` for the AMS
         * one).
         */
        self.LOCK_NAME_PREFIXES = {
            sale: 'sales',
            eoi: 'eois',
            ams_sale: 'sales',
            ams_eoi: 'eois'
        };
    }

    /**
     * Build the fully qualified lock name the server would generate.
     *
     * @remarks
     * Acquire returns the authoritative `lock_name` in its payload and callers
     * should keep that value. This helper only exists for the paths that need
     * a lock name without having acquired one in the same controller (for
     * example a review page that inherits a lock acquired on the listing).
     *
     * @param {string} entityType One of ENTITY_TYPE_SALE / ENTITY_TYPE_EOI.
     * @param {number} entityId   Sale or EOI id.
     * @param {number} lockType   LOCK_TYPE_* constant.
     * @returns {string} e.g. `sales.123.10`
     */
    getLockName(entityType, entityId, lockType) {
        const self = this;

        let prefix = self.LOCK_NAME_PREFIXES[entityType] || entityType;

        return prefix + '.' + entityId + '.' + lockType;
    }

    /**
     * Build the API path for an operation on an existing lock.
     *
     * @remarks
     * The lock name is a path segment, so it is percent-encoded rather than
     * concatenated raw. A well-formed name (`sales.77.10`) is unchanged by the
     * encoding — this only stops a malformed or unexpected value from
     * rewriting the path.
     *
     * @param {string} lockName  Value returned as `data.lock_name` by acquire.
     * @param {string} operation `ping` or `release`.
     * @returns {string} e.g. `sec/locks/sales.77.10/ping`
     */
    getLockPath(lockName, operation) {
        return 'sec/locks/' + encodeURIComponent(lockName) + '/' + operation;
    }

    /**
     * Acquire a lock on a record.
     *
     * @param {string} entityType One of ENTITY_TYPE_SALE / ENTITY_TYPE_EOI.
     * @param {number} entityId   Sale or EOI id.
     * @param {number} lockType   LOCK_TYPE_* constant.
     * @param {Object} [options]  Optional `override` (1 to force-release a
     *                            lock held by another user), `timeout`
     *                            (seconds, server default 300) and
     *                            `session_id`.
     * @returns {Promise} Resolves with the response (`data.lock_name`,
     *                    `data.expires_at`, `data.locked_by`) or with an Error
     *                    carrying `sub_code` / `message`.
     */
    acquire(entityType, entityId, lockType, options) {
        const self = this;

        let params = angular.extend({
            entityType: entityType,
            entityId: entityId,
            lock_type: lockType
        }, options || {});

        return self.SSHttpService.getAPIRequest('sec/locks/acquire', params);
    }

    /**
     * Extend a held lock (heartbeat).
     *
     * @param {string} lockName Value returned as `data.lock_name` by acquire.
     * @param {Object} [options] Optional `responded` (0 releases the lock
     *                           instead of extending it) and `timeout`.
     * @returns {Promise} Resolves with the response or an Error whose
     *                    `sub_code` may be SUB_CODE_LOCK_CONFLICT,
     *                    SUB_CODE_LOCK_NOT_FOUND or SUB_CODE_LOCK_EXPIRED.
     */
    ping(lockName, options) {
        const self = this;

        return self.SSHttpService.getAPIRequest(
            self.getLockPath(lockName, 'ping'),
            options || null
        );
    }

    /**
     * Release a held lock so the record is immediately available to others.
     *
     * @param {string} lockName Value returned as `data.lock_name` by acquire.
     * @param {string} [reason] Free text stored on the lock audit trail.
     * @returns {Promise} Resolves with the response or an Error. Callers
     *                    normally ignore the result — a lock that already
     *                    expired returns SUB_CODE_LOCK_NOT_FOUND, which is not
     *                    worth surfacing to the user.
     */
    release(lockName, reason) {
        const self = this;

        let params = {};
        if (reason) {
            params.reason = reason;
        }

        return self.SSHttpService.postAPIRequest(
            self.getLockPath(lockName, 'release'),
            params
        );
    }

    /**
     * Release a lock while the page is being torn down.
     *
     * @remarks
     * An `$http` POST issued from `beforeunload` or immediately before
     * `location.href = …` is routinely cancelled by the navigation, which
     * would leave the record locked for the full 300-second expiry.
     * `navigator.sendBeacon` is queued by the browser and survives the
     * unload. Auth rides along in the query string (SSHttpService appends
     * `api_token` in `getApiURL`), so no credentials or headers are needed,
     * and a `text/plain` body keeps it a CORS-simple request — sendBeacon
     * cannot answer a preflight.
     *
     * Falls back to the ordinary {@link release} when sendBeacon is
     * unavailable.
     *
     * @param {string} lockName Value returned as `data.lock_name` by acquire.
     * @param {string} [reason] Free text stored on the lock audit trail.
     * @returns {boolean} True when the beacon was queued.
     */
    releaseOnUnload(lockName, reason) {
        const self = this;

        if (!lockName) {
            return false;
        }

        let navigatorRef = self.$window.navigator;

        if (!(navigatorRef && typeof navigatorRef.sendBeacon === 'function')) {
            self.release(lockName, reason);
            return false;
        }

        let url = self.SSHttpService.getApiURL(self.getLockPath(lockName, 'release'));
        if (reason) {
            url += '&reason=' + encodeURIComponent(reason);
        }

        try {
            return navigatorRef.sendBeacon(url, new Blob([], {type: 'text/plain'}));
        } catch (e) {
            self.release(lockName, reason);
            return false;
        }
    }

    /**
     * Name of the user currently holding a contested lock.
     *
     * @param {Error|Object} response Rejected acquire/ping response carrying
     *                                `data.locking_user`.
     * @returns {string} Display name, or "other user" when the server did not
     *                   identify the holder.
     */
    getLockingUserName(response) {
        let lockingUser = response && response.data && response.data.locking_user;

        if (!lockingUser) {
            return 'other user';
        }

        if (lockingUser.display_name) {
            return lockingUser.display_name;
        }

        return lockingUser.first_name + ' ' + lockingUser.last_name;
    }

}

SSLockService.$inject = ['$window', 'SSHttpService'];
app.service('SSLockService', SSLockService);
